<template>
    <div class="apple-login-wrap">
        <button
            type="button"
            class="apple-login-btn"
            :class="{ 'apple-login-btn--icon': type === 'icon' }"
            :disabled="loading || !appleClientId || appleLinkModalOpen"
            @click="handleAppleLogin"
        >
            <img
                :src="appleLogoSrc"
                alt=""
                class="apple-login-btn__logo"
                aria-hidden="true"
            />
            <span v-if="type !== 'icon'" class="apple-login-btn__text">
                {{ buttonText }}
            </span>
        </button>
        <ui-modal
            ref="appleLinkModal"
            class="apple-link-modal"
            centered
            :title="$t('Sign in with Apple — choose an option')"
            @hidden="onAppleLinkModalHidden"
        >
            <template v-if="!appleLinkOtpSent">
                <p class="apple-link-modal__info-text">
                    {{ $t('Apple is using a private email for this sign-in. You can link it to your current account or create a new account with this email.') }}
                </p>
                <p class="apple-link-modal__info-text">
                    {{ $t('To link your existing account: enter the email of your current account below. We will send you a verification code to complete the link.') }}
                </p>
            </template>
            <p v-else class="apple-link-modal__info-text">
                {{ $t('We sent a verification code to your email. Enter it below to link your account. If you do not see it, please check your spam or junk folder.') }}
            </p>
            <form @submit.prevent="submitAppleLink">
                <div class="apple-link-modal__field">
                    <ui-input
                        id="apple-link-email"
                        name="appleLinkEmail"
                        type="email"
                        v-model="appleLinkForm.email"
                        :label="$t('Email')"
                        :field="v$.appleLinkForm.email"
                        :disabled="appleLinkOtpSent"
                        placeholder="email@example.com"
                        autocomplete="email"
                    />
                </div>
                <div v-if="appleLinkOtpSent" class="apple-link-modal__field">
                    <ui-input
                        id="apple-link-code"
                        name="appleLinkCode"
                        type="text"
                        v-model="appleLinkForm.code"
                        :label="$t('Verification code')"
                        :field="v$.appleLinkForm.code"
                        placeholder="000000"
                        :maxlength="6"
                        autocomplete="one-time-code"
                        inputmode="numeric"
                    />
                </div>
            </form>
            <vue-recaptcha
                v-show="false"
                ref="appleLinkRecaptcha"
                size="invisible"
                :sitekey="recaptchaSiteKey"
                :loadRecaptchaScript="true"
                @verify="onAppleLinkRecaptchaVerify"
            />
            <div class="apple-link-modal__actions">
                <ui-button
                    v-if="!appleLinkOtpSent"
                    class="apple-link-modal__action"
                    :loading="appleLinkSubmitting"
                    @click="sendAppleLinkOtpStep"
                >
                    {{ $t('Link account') }}
                </ui-button>
                <ui-button
                    v-else
                    class="apple-link-modal__action"
                    :loading="appleLinkSubmitting"
                    @click="submitAppleLink"
                >
                    {{ $t('Link account and sign in') }}
                </ui-button>
                <ui-button
                    v-if="!appleLinkOtpSent"
                    variant="secondary"
                    class="apple-link-modal__action"
                    :disabled="appleLinkSubmitting"
                    @click="createNewAppleAccount"
                >
                    {{ $t('Create new account') }}
                </ui-button>
            </div>
        </ui-modal>
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { email, helpers, maxLength, minLength, required } from '@vuelidate/validators';
import VueRecaptcha from 'vue-recaptcha';

import UiButton from '@/components/ui/buttons/Button.vue';
import UiInput from '@/components/ui/inputs/Input.vue';
import UiModal from '@/components/ui/modal/Modal.vue';

import socialLoginMixin from '@/plugins/mixin/social-login.mixin';
import appleLoginMixin from '@/plugins/mixin/apple-login.mixin';
import recaptchaUtils from '@/utils/recaptcha.utils';

const APPLE_SCRIPT_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

function getAppleIdTokenPayload(idToken) {
    if (!idToken || typeof idToken !== 'string') {
        return null;
    }
    try {
        const payload = idToken.split('.')[1];
        if (!payload) {
            return null;
        }
        return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
}

function isApplePrivateEmail(idToken) {
    const payload = getAppleIdTokenPayload(idToken);
    return Boolean(payload && payload.is_private_email === true);
}

export default {
    name: 'AppleLoginButton',
    mixins: [socialLoginMixin, appleLoginMixin],
    components: {
        UiButton,
        UiInput,
        UiModal,
        VueRecaptcha,
    },
    setup() {
        return { v$: useVuelidate() };
    },
    props: {
        text: {
            type: String,
            default: 'signin_with',
            validator: (value) => ['continue_with', 'signin_with', 'signup_with'].includes(value)
        },
        type: {
            type: String,
            default: 'standard'
        }
    },
    data() {
        return {
            user: {
                _tk: null,
                siteId: null,
                redirectUrl: null,
            },
            loading: false,
            appleReady: false,
            appleLinkModalOpen: false,
            appleLinkSubmitting: false,
            appleLinkForm: { email: '', code: '' },
            appleLinkOtpSent: false,
            pendingAppleUser: null,
        };
    },
    validations() {
        return {
            appleLinkForm: {
                email: {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('Email') }), required),
                    email: helpers.withMessage(this.$t('Email invalid'), email),
                    $autoDirty: true,
                },
                code: {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('Verification code') }), required),
                    minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params), minLength(6)),
                    maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params), maxLength(6)),
                    regex: helpers.withMessage(this.$t('The code must be 6 digits'), helpers.regex(/^\d{6}$/)),
                    $autoDirty: true,
                },
            },
        };
    },
    computed: {
        buttonText() {
            const texts = {
                'continue_with': this.$t('Continue with Apple'),
                'signin_with': this.$t('Sign in with Apple'),
                'signup_with': this.$t('Sign up with Apple')
            };
            return texts[this.text] || texts['signin_with'];
        },
        appleLogoSrc() {
            return this.type === 'icon'
                ? require('@/assets/images/apple/sign-in-with-apple-logo-only.svg')
                : require('@/assets/images/apple/sign-in-with-apple-left-aligned.svg');
        },
        recaptchaSiteKey() {
            return globalThis?.ENV?.VUE_APP_RECAPTCHA || '';
        }
    },
    mounted() {
        this.user.siteId = this.$route.query.site_id;
        this.user.redirectUrl = this.$route.query.redirect_url;
        this.loadAppleScript();
    },
    methods: {
        loadAppleScript() {
            if (typeof globalThis.window === 'undefined' || globalThis.AppleID) {
                this.appleReady = true;
                return;
            }
            const existing = document.querySelector(`script[src="${APPLE_SCRIPT_URL}"]`);
            if (existing) {
                this.appleReady = true;
                return;
            }
            const script = document.createElement('script');
            script.src = APPLE_SCRIPT_URL;
            script.async = true;
            script.onload = () => {
                this.appleReady = true;
            };
            script.onerror = () => {
                this.$toasted.global.errorMessage(this.$t('Apple Sign In could not be loaded'));
            };
            document.head.appendChild(script);
        },
        getRedirectURI() {
            return globalThis?.location?.origin;
        },
        initApple() {
            if (!this.appleClientId) {
                return;
            }
            if (globalThis.AppleID && globalThis.AppleID.auth && !globalThis.AppleID._accountsInit) {
                globalThis.AppleID.auth.init({
                    clientId: this.appleClientId,
                    scope: 'name email',
                    redirectURI: this.getRedirectURI(),
                    usePopup: true,
                    state: globalThis.btoa(JSON.stringify({
                        site_id: this.user.siteId,
                        redirect_url: this.user.redirectUrl
                    }))
                });
                globalThis.AppleID._accountsInit = true;
            }
        },
        handleAppleLoginNoToken(response) {
            if (response?.error && response.error !== 'popup_closed_by_user') {
                this.$toasted.global.errorMessage(this.$t(response.error));
            } else if (!response?.error) {
                this.$toasted.global.errorMessage(this.$t('Apple Sign In did not return a valid token'));
            }
        },
        async handleAppleLogin() {
            if (!this.appleReady || !globalThis.AppleID) {
                this.$toasted.global.errorMessage(this.$t('Apple Sign In is still loading. Please try again.'));
                return;
            }
            try {
                this.loading = true;
                this.initApple();
                const response = await globalThis.AppleID.auth.signIn();
                const id_token = response?.authorization?.id_token;
                if (!id_token) {
                    this.handleAppleLoginNoToken(response);
                    return;
                }
                this.user._tk = id_token;
                const name = response?.user?.name;
                if (name?.firstName) {
                    this.user.first_name = name.firstName;
                }
                if (name?.lastName) {
                    this.user.last_name = name.lastName;
                }
                await this.socialNetWorkSucess(this.user);
            } catch (err) {
                if (err.appleLinkRequired) {
                    this.pendingAppleUser = { ...this.user };
                    this.appleLinkModalOpen = true;
                    this.$nextTick(() => this.$refs.appleLinkModal?.show());
                    return;
                }
                const error_code = err?.error;
                if (error_code !== 'popup_closed_by_user') {
                    this.showError(err);
                }
            } finally {
                this.loading = false;
            }
        },
        onAppleLinkModalHidden() {
            this.appleLinkForm = { email: '', code: '' };
            this.appleLinkOtpSent = false;
            this.pendingAppleUser = null;
            this.appleLinkModalOpen = false;
            this.v$.$reset();
        },
        closeAppleLinkModal() {
            this.$refs.appleLinkModal?.close();
        },
        sendAppleLinkOtpStep() {
            if (!this.pendingAppleUser) {
                return;
            }
            this.v$.appleLinkForm.email.$touch();
            if (this.v$.appleLinkForm.email.$invalid) {
                return;
            }
            const email = this.appleLinkForm.email.trim();
            if (this.isRunningInWebView || !recaptchaUtils.isAvailable()) {
                this.doSendAppleLinkOtp(email, '');
                return;
            }
            this.$refs.appleLinkRecaptcha.reset();
            this.$refs.appleLinkRecaptcha.execute();
        },
        async onAppleLinkRecaptchaVerify(token) {
            this.v$.appleLinkForm.email.$touch();
            if (this.v$.appleLinkForm.email.$invalid || !this.pendingAppleUser) {
                return;
            }
            const email = this.appleLinkForm.email.trim();
            await this.doSendAppleLinkOtp(email, token);
        },
        async doSendAppleLinkOtp(email, token) {
            this.appleLinkSubmitting = true;
            try {
                await this.sendAppleLinkOtp(email, token);
                this.appleLinkOtpSent = true;
                this.$toasted.global.successMessage(this.$t('If an account exists for that email, we sent a verification code to link it with Sign in with Apple.'));
            } catch (err) {
                this.showError(err);
            } finally {
                this.appleLinkSubmitting = false;
            }
        },
        async submitAppleLink() {
            if (!this.pendingAppleUser || !this.appleLinkOtpSent) {
                return;
            }
            this.v$.appleLinkForm.code.$touch();
            if (this.v$.appleLinkForm.code.$invalid) {
                return;
            }
            this.appleLinkSubmitting = true;
            try {
                await this.submitAppleLinkExisting(
                    this.pendingAppleUser,
                    this.appleLinkForm.email.trim(),
                    this.appleLinkForm.code.trim()
                );
                this.closeAppleLinkModal();
            } catch (err) {
                this.showError(err);
            } finally {
                this.appleLinkSubmitting = false;
            }
        },
        async createNewAppleAccount() {
            if (!this.pendingAppleUser) {
                return;
            }
            this.appleLinkSubmitting = true;
            try {
                await this.socialNetWorkSucess(this.pendingAppleUser, { apple_confirm_new_account: true });
                this.closeAppleLinkModal();
            } catch (err) {
                this.showError(err);
            } finally {
                this.appleLinkSubmitting = false;
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.apple-link-modal {
    &__info-text {
        color: #555555;
        font-size: 1rem;
        margin-bottom: 1rem;
    }
    &__field {
        margin-bottom: 1rem;
    }
    &__actions {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 1rem;
    }
    &__action {
        width: 100%;
    }
}

.apple-login-wrap {
    display: inline-block;
}
.apple-login-btn {
    align-items: center;
    background-color: #000000;
    border: 1px solid #000000;
    border-radius: 4px;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    height: 40px;
    justify-content: center;
    line-height: 110%;
    max-width: 100%;
    padding: 0 12px;
    transition: 0.1s all;
    width: 220px;
    flex-wrap: nowrap;

    &:hover:not(:disabled) {
        background-color: #1a1a1a;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.7;
    }

    &--icon {
        width: 44px;
        padding: 0;
    }

    &__logo {
        height: 40px;
        width: 40px;
        flex-shrink: 0;
        object-fit: contain;
    }

    &--icon &__logo {
        height: 40px;
        width: 40px;
        margin-right: 0;
    }

    &__text {
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0.25px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: auto;
    }
}
</style>
